import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskLines } from "@/components/MaskLines";
import { HistorySection } from "@/pages/History";
import { ProjectSection } from "@/pages/Project";
import { useLang } from "@/lib/i18n";

const JUMPS = [["que-es", "about.jump.what"], ["mi-historia", "about.jump.story"], ["proyecto", "about.jump.project"]];

/** Floating pill that jumps between the three blocks and highlights the one in view. */
const JumpNav = () => {
  const { t } = useLang();
  const [active, setActive] = useState("que-es");
  useEffect(() => {
    const els = JUMPS.map(([id]) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    }, { rootMargin: "-45% 0px -50% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (
    <nav className="about-jump" aria-label={t("nav.about")} data-testid="about-jump-nav">
      {JUMPS.map(([id, key]) => (
        <a key={id} href={`#${id}`} className={`about-jump-item ${active === id ? "about-jump-on" : ""}`} aria-current={active === id ? "true" : undefined} data-testid={`about-jump-${id}`}>{t(key)}</a>
      ))}
    </nav>
  );
};

const Intro = () => {
  const { t } = useLang();
  return (
    <section id="que-es" className="mx-auto max-w-6xl px-5 pt-32 md:px-10" data-testid="about-intro">
      <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("about.kicker")}</p>
      <MaskLines as="h1" className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" lines={[t("about.l1"), <em key="e">{t("about.l2")}</em>]} />
      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div className="space-y-4 text-sm leading-relaxed text-[var(--muted)] md:text-base">
          <p data-testid="about-body-1">{t("about.p1")}</p>
          <p data-testid="about-body-2">{t("about.p2")}</p>
          <div className="flex flex-wrap gap-3 pt-4">
            <Link to="/explorar?mode=3d" className="btn-primary" data-testid="about-explore-btn">{t("about.cta")} <ArrowRight size={16} /></Link>
            <a href="#mi-historia" className="btn-ghost" data-testid="about-story-btn">{t("about.jump.story")}</a>
            <a href="#proyecto" className="btn-ghost" data-testid="about-project-btn">{t("about.jump.project")}</a>
          </div>
        </div>
        <ul className="grid grid-cols-2 gap-3 self-start" data-testid="about-facts">
          {["f1", "f2", "f3", "f4"].map((k) => (
            <li key={k} className="rounded-2xl border border-white/10 bg-[var(--card)] px-4 py-5">
              <p className="font-display text-3xl leading-none text-[var(--green)]">{t(`about.${k}.n`)}</p>
              <p className="mono mt-2 text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]">{t(`about.${k}.l`)}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default function About() {
  return (
    <main data-testid="about-page">
      <Intro />
      <HistorySection />
      <ProjectSection />
      <JumpNav />
    </main>
  );
}
